import {
  Component,
  effect,
  ElementRef,
  HostListener,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  QueryList,
  ViewChildren
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {Subject, Subscription} from 'rxjs';

@Component({
  selector: 'app-otp-input',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule
  ],
  templateUrl: './otp-input.component.html',
  styleUrls: ['./otp-input.component.css']
})
export class OtpInputComponent {
  private readonly onDestroy: Subject<any> = new Subject<any>();
  label: InputSignal<any> = input<string>('');
  error: InputSignal<any> = input<string>('');
  placeholder: InputSignal<any> = input<string>('');
  parentFormGroup: InputSignal<any> = input<FormGroup>();
  controlName: InputSignal<any> = input<string>();
  checkForErrors: OutputEmitterRef<any> = output<void>();


  isRequired: boolean = false;
  isDisabled: boolean = false;

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

  otpForm: FormGroup;
  focusedIndex: number = 0;

  fieldSubscription: Subscription | undefined;

  @HostListener("focusout", ["$event"])
  onBlur() {
    if (!this.error()) {
      // @ts-ignore
      this.parentFormGroup()?.controls[this.controlName()].markAsUntouched();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const pastedText = clipboardData.getData('text').trim();

      // Check if pasted text is a number and has expected length
      if (/^\d+$/.test(pastedText) && pastedText.length <= 6) {
        // Fill in the OTP inputs
        for (let i = 0; i < Math.min(pastedText.length, 6); i++) {
          this.getDigitControl(i).setValue(pastedText[i]);
        }

        // Focus on the next empty field or the last field
        const nextEmptyIndex = Math.min(pastedText.length, 5);
        if (nextEmptyIndex < 6) {
          setTimeout(() => {
            this.focusedIndex = nextEmptyIndex;
            this.otpInputs.toArray()[nextEmptyIndex].nativeElement.focus();
          });
        }
      }
    }
  }

  constructor(private fb: FormBuilder) {
    this.otpForm = this.fb.group({
      digit0: new FormControl('', [Validators.required, Validators.pattern('^[0-9]$')]),
      digit1: new FormControl('', [Validators.required, Validators.pattern('^[0-9]$')]),
      digit2: new FormControl('', [Validators.required, Validators.pattern('^[0-9]$')]),
      digit3: new FormControl('', [Validators.required, Validators.pattern('^[0-9]$')]),
      digit4: new FormControl('', [Validators.required, Validators.pattern('^[0-9]$')]),
      digit5: new FormControl('', [Validators.required, Validators.pattern('^[0-9]$')])
    });
  }

  ngOnInit(): void {
    // Subscribe to form value changes
    this.otpForm.valueChanges.subscribe(value => {
      const otp = Object.values(value).join('');
      const parentForm = this.parentFormGroup();
      const control = this.controlName();
      if (parentForm && control) {
        parentForm.get(control)?.setValue(otp);
      }
    });

    // @ts-ignore
    const control = this.parentFormGroup()?.controls[this.controlName()];

    this.isDisabled = control?.status === "DISABLED";
    this.fieldSubscription = control?.valueChanges?.subscribe(() => {
      // If field is marked as touched, mark it as untouched
      if (control?.touched) {
        control?.markAsUntouched();
      }

      // Get errors on the form field
      const errors = control?.errors;

      // If errors exist, handle it
      if (errors) {
        // Clear errors on the form field
        control?.setErrors(null);

        // Clear out the error messages on the screen (parent component)
        this.checkForErrors?.emit(this.controlName());

        // Add errors again to the form field for showing messages in the future
        control?.setErrors(errors);
      } else {
        // Clear out the error messages on the screen (parent component)
        this.checkForErrors?.emit(this.controlName());
      }
    });

    // Check if field is required
    if (control?.validator) {
      const validator = control.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator["required"]);
    }
  }

  getDigitControl(index: number): FormControl {
    return this.otpForm.get(`digit${index}`) as FormControl;
  }

  onInput(event: any, index: number): void {
    const value = event.target.value;

    // Only allow numbers
    if (!/^\d*$/.test(value)) {
      event.target.value = '';
      return;
    }

    // Always take the last character entered (ensures replacement behavior)
    const lastChar = value.charAt(value.length - 1);
    this.getDigitControl(index).setValue(lastChar);

    // Move to next input if current input is filled
    if (lastChar && index < 5) {
      this.focusedIndex = index + 1;
      setTimeout(() => {
        this.otpInputs.toArray()[this.focusedIndex].nativeElement.focus();
      });
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    const key = event.key;
    const selectionStart = input.selectionStart;

    // Handle backspace logic
    if (key === 'Backspace') {
      // If the current box is empty and not the first box
      if (!this.getDigitControl(index).value && index > 0) {
        event.preventDefault();
        this.focusedIndex = index - 1;
        
        // Clear the previous box and focus on it
        setTimeout(() => {
          this.getDigitControl(this.focusedIndex).setValue('');
          const prevInput = this.otpInputs.toArray()[this.focusedIndex].nativeElement;
          prevInput.focus();
        });
        return;
      }
      
      // If there's content in the current box or selection, let default behavior handle it
      if (this.getDigitControl(index).value && (selectionStart === null || selectionStart > 0)) {
        return;
      }

      // If the box is empty or cursor is at start position, go to previous box
      if (index > 0) {
        event.preventDefault();
        this.focusedIndex = index - 1;
        setTimeout(() => {
          const prevInput = this.otpInputs.toArray()[this.focusedIndex].nativeElement;
          prevInput.focus();
          // Position cursor at the end
          prevInput.setSelectionRange(1, 1);
        });
      }
    }

    // Handle delete key
    else if (key === 'Delete') {
      // If at the last character or selection exists, let default behavior handle it
      if (selectionStart !== null && selectionStart < input.value.length && input.selectionEnd !== input.selectionStart) {
        return;
      }

      // If at the end, move to next input
      if (index < 5 && selectionStart === input.value.length) {
        event.preventDefault();
        this.focusedIndex = index + 1;
        setTimeout(() => {
          const nextInput = this.otpInputs.toArray()[this.focusedIndex].nativeElement;
          nextInput.focus();
          // Position cursor at the start
          nextInput.setSelectionRange(0, 0);
        });
      }
    }

    // Handle left arrow
    else if (key === 'ArrowLeft') {
      // If not at the start, let default behavior occur
      if (selectionStart !== null && selectionStart > 0) {
        return;
      }

      // If at the beginning of the input, go to previous input
      if (index > 0) {
        event.preventDefault();
        this.focusedIndex = index - 1;
        setTimeout(() => {
          const prevInput = this.otpInputs.toArray()[this.focusedIndex].nativeElement;
          prevInput.focus();
          // Position cursor at the end
          prevInput.setSelectionRange(1, 1);
        });
      }
    }

    // Handle right arrow
    else if (key === 'ArrowRight') {
      // If not at the end, let default behavior occur
      if (selectionStart !== null && selectionStart < input.value.length) {
        return;
      }

      // If at the end of the input, go to next input
      if (index < 5) {
        event.preventDefault();
        this.focusedIndex = index + 1;
        setTimeout(() => {
          const nextInput = this.otpInputs.toArray()[this.focusedIndex].nativeElement;
          nextInput.focus();
          // Position cursor at the beginning
          nextInput.setSelectionRange(0, 0);
        });
      }
    }
  }

  onFocus(index: number): void {
    this.focusedIndex = index;
    
    // Select all text in the input when focused, making it easier to replace
    setTimeout(() => {
      const input = this.otpInputs.toArray()[index].nativeElement;
      if (input.value) {
        input.select();
      }
    });
  }

  ngOnDestroy() {
    this.fieldSubscription?.unsubscribe();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
